import React, { useMemo, useState } from 'react'
import { Send, Link as LinkIcon } from 'lucide-react'
import { PartnerService, UserService } from '@duck/api-definitions'
import { Skeleton } from '@duck/ui'
import { useTar } from '@/modules/i18n'
import { buildAssignmentGroups, type AssignmentGroup } from '../cohortV2Data'
import { AssignmentRow } from './AssignmentRow'
import { SubmissionResultsDialog, type ResultsDetails } from './SubmissionResultsDialog'

interface AssignmentsTabProps {
  assignments: PartnerService.Assignment[]
  assignmentsDetails: PartnerService.AssignmentDetails[]
  persons: UserService.Person[]
  isLoading: boolean
  selectedTaskIds: string[]
  onAssignmentChange: () => void
}

interface SectionProps {
  icon: React.ReactNode
  label: string
  groups: AssignmentGroup[]
  persons: UserService.Person[]
  onViewResults: (d: ResultsDetails) => void
  onAssignmentChange: () => void
}

// Module-level (NOT defined inside AssignmentsTab): a stable component identity is required so
// that AssignmentsTab state changes (e.g. opening the results dialog) re-render rather than
// REMOUNT the rows — remounting would reset each row's expand state and collapse the roster.
const Section: React.FC<SectionProps> = ({
  icon, label, groups, persons, onViewResults, onAssignmentChange,
}) => {
  if (groups.length === 0) return null
  return (
    <div>
      <div className="mb-2.5 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        {icon}{label}<span className="font-semibold text-muted-foreground/70">· {groups.length}</span>
      </div>
      {groups.map((g) => (
        <AssignmentRow
          key={g.assignment.id}
          group={g}
          persons={persons}
          onViewResults={onViewResults}
          onAssignmentChange={onAssignmentChange}
        />
      ))}
    </div>
  )
}

export const AssignmentsTab: React.FC<AssignmentsTabProps> = ({
  assignments, assignmentsDetails, persons, isLoading, selectedTaskIds, onAssignmentChange,
}) => {
  const { t } = useTar()
  const [resultsOpen, setResultsOpen] = useState(false)
  const [results, setResults] = useState<ResultsDetails | null>(null)

  // Filter to the selected challenges; an empty selection means "all challenges".
  const visibleAssignments = useMemo(
    () => assignments.filter((a) => selectedTaskIds.length === 0 || selectedTaskIds.includes(a.taskId)),
    [assignments, selectedTaskIds]
  )

  const { publicGroups, sentGroups } = useMemo(
    () => buildAssignmentGroups(visibleAssignments, assignmentsDetails),
    [visibleAssignments, assignmentsDetails]
  )

  const onViewResults = (d: ResultsDetails) => { setResults(d); setResultsOpen(true) }

  if (isLoading) {
    return <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[62px] w-full rounded-xl" />)}</div>
  }

  return (
    <div className="space-y-6">
      <Section
        icon={<LinkIcon className="h-3.5 w-3.5 text-primary" />}
        label={t('partner.publicAssignments')}
        groups={publicGroups}
        persons={persons}
        onViewResults={onViewResults}
        onAssignmentChange={onAssignmentChange}
      />
      <Section
        icon={<Send className="h-3.5 w-3.5" />}
        label={t('partner.sentAssignments')}
        groups={sentGroups}
        persons={persons}
        onViewResults={onViewResults}
        onAssignmentChange={onAssignmentChange}
      />
      {publicGroups.length === 0 && sentGroups.length === 0 && (
        <p className="py-10 text-center text-sm text-muted-foreground">{t('common.empty')}</p>
      )}
      <SubmissionResultsDialog open={resultsOpen} onOpenChange={setResultsOpen} details={results} />
    </div>
  )
}
